import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaEnvelope,
  FaStar,
  FaSignOutAlt,
  FaArrowLeft,
  FaBars,
  FaTimes,
  FaShieldAlt,
  FaCode,
  FaVideo,
  FaUserCog,
} from 'react-icons/fa';
import logo from '../../../assets/arvix/arvix_logo.png';

const NAV_ITEMS = [
  { key: 'enrollments', label: 'Enrollments', icon: FaUsers },
  { key: 'contacts', label: 'Messages', icon: FaEnvelope },
  { key: 'reviews', label: 'Reviews', icon: FaStar },
  { key: 'accounts', label: 'Teachers & Students', icon: FaUserCog },
  { key: 'courses', label: 'Courses', icon: FaVideo },
];

/** Admin tab keys mapped to their routes. */
export const TAB_ROUTES = {
  enrollments: '/admin/enrollments',
  contacts: '/admin/messages',
  reviews: '/admin/reviews',
  accounts: '/admin/accounts',
  courses: '/admin/courses',
};

/** Resolve which admin tab a pathname belongs to (null outside the tab routes). */
export const tabFromPath = (pathname = '') => {
  if (pathname.includes('/admin/reviews')) return 'reviews';
  if (pathname.includes('/admin/messages') || pathname.includes('/admin/contacts')) return 'contacts';
  if (pathname.includes('/admin/accounts')) return 'accounts';
  if (pathname.includes('/admin/courses')) return 'courses';
  if (pathname.startsWith('/admin')) return 'enrollments';
  return null;
};

const AdminLayout = ({
  activeTab,
  onTabChange,
  onLogout,
  username,
  roleLabel = 'Administrator',
  userIcon: UserIcon = FaShieldAlt,
  panelLabel = 'Admin Console',
  stats,
  children,
  title,
  subtitle,
  /** Hide the admin tab nav (e.g. teachers inside Course Studio). */
  showTabs = true,
  /** Custom nav links [{ to, label, icon, active }] instead of tabs (e.g. teacher/student dashboards). */
  navItems = null,
  /** Action buttons rendered at the right of the header (desktop, or mobile when tabs are hidden). */
  headerActions = null,
  /** Back button target, e.g. { to: '/admin/studio', label: 'Back' }. */
  backTo = null,
}) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const getBadgeCount = (key) => {
    if (!stats) return null;
    if (key === 'enrollments') return stats.enrollments;
    if (key === 'contacts') return stats.contacts;
    if (key === 'reviews') return stats.reviews;
    return null;
  };

  const activeItem = NAV_ITEMS.find((item) => item.key === activeTab);
  const headerTitle = activeItem?.label || title || 'Admin';
  const headerSubtitle = subtitle || (activeItem ? 'Manage your Creative Samurai program' : null);

  const NavContent = ({ onNavigate }) => (
    <>
      <div className="px-4 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Arvix Creative" className="h-8 w-auto brightness-0 invert" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-white leading-tight">Arvix Creative</p>
            <p className="text-[11px] text-white/50 leading-tight">{panelLabel}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Admin navigation">
        {showTabs && NAV_ITEMS.map(({ key, label, icon: Icon }) => {
          const isActive = activeTab === key;
          const count = getBadgeCount(key);

          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                onTabChange(key);
                onNavigate?.();
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? 'bg-designer-primary text-white shadow-lg shadow-designer-primary/25'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : 'text-white/60'}`} />
              <span className="flex-1 text-left">{label}</span>
              {count != null && (
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center ${
                    isActive ? 'bg-white/20 text-white' : 'bg-white/10 text-white/80'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        {navItems?.map(({ to, label, icon: Icon, active }) => (
          <Link
            key={to + label}
            to={to}
            onClick={() => onNavigate?.()}
            aria-current={active ? 'page' : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
              active
                ? 'bg-designer-primary text-white shadow-lg shadow-designer-primary/25'
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-white/60'}`} />
            <span className="flex-1 text-left">{label}</span>
          </Link>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-3 py-2.5 mb-2">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <UserIcon className="w-3.5 h-3.5 text-white/70" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white truncate">{username || 'Admin'}</p>
            <p className="text-[10px] text-white/40">{roleLabel}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-red-500/20 transition-all"
        >
          <FaSignOutAlt className="w-4 h-4" />
          Sign out
        </button>
      </div>

      <div className="px-3 pb-4">
        <a
          href="https://junaidul.pro.bd/codejborg"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Developed by CodeJBorg: visit developer website"
          className="mt-4 flex items-center justify-center gap-1.5 border-t border-white/10 pt-3 font-mono text-[11px] text-white/55 transition-colors hover:text-white"
        >
          <FaCode className="size-3.5" aria-hidden="true" />
          <span>Developed by</span>
          <span className="font-semibold text-white">
            <span className="text-designer-primary">&lt;</span>CodeJBorg<span className="text-designer-primary"> /&gt;</span>
          </span>
        </a>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-designer-dark z-40">
        <NavContent />
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-designer-dark flex flex-col shadow-2xl animate-slide-up">
            <div className="flex items-center justify-end px-4 pt-4">
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10"
                aria-label="Close menu"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <NavContent onNavigate={() => setMobileNavOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-200/80 px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              {backTo && (
                <Link
                  to={backTo.to}
                  aria-label={backTo.label || 'Back'}
                  title={backTo.label || 'Back'}
                  className="p-2 rounded-lg text-gray-500 hover:text-designer-primary hover:bg-gray-100 shrink-0 transition-colors"
                >
                  <FaArrowLeft className="w-4 h-4" />
                </Link>
              )}
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 -ml-1 rounded-lg text-gray-500 hover:text-designer-dark hover:bg-gray-100 shrink-0"
                aria-label="Open navigation"
              >
                <FaBars className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-designer-dark truncate" title={headerTitle}>
                  {headerTitle}
                </h1>
                {headerSubtitle && (
                  <p className="text-xs text-gray-400 hidden sm:block">
                    {headerSubtitle}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {headerActions && (
                <div className={`items-center gap-2 ${showTabs ? 'hidden lg:flex' : 'flex'}`}>
                  {headerActions}
                </div>
              )}
              {showTabs && (
                <div className="flex lg:hidden gap-0.5 sm:gap-1 p-1 bg-gray-100 rounded-lg shrink-0">
                  {NAV_ITEMS.map(({ key, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onTabChange(key)}
                      className={`p-1.5 sm:p-2 rounded-md transition-all ${
                        activeTab === key
                          ? 'bg-white text-designer-primary shadow-sm'
                          : 'text-gray-400'
                      }`}
                      aria-label={key}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 py-5 sm:py-6">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
